import { useState, useEffect, useRef, useCallback } from 'react';
import { isAbortError } from '@medprompt/shared';

/**
 * Patient names for the table rows on screen, fetched in one bulk request per page and cached by
 * NHS id. `service.fetchPatientsData(ids)` resolves to a map from each row's `idKey` value to its patient.
 */
const useRowPatientNames = ({ service, idKey }) => {
  const [nhsIdToPatientData, setNhsIdToPatientData] = useState(new Map());
  // Read by the row-hydration callback, which must stay reference-stable to avoid re-triggering
  // the very effect that calls it.
  const nhsIdToPatientDataRef = useRef(nhsIdToPatientData);
  useEffect(() => {
    nhsIdToPatientDataRef.current = nhsIdToPatientData;
  }, [nhsIdToPatientData]);

  // One controller for the current page's bulk hydration. Replaced (and aborted) when the
  // visible uncached set changes.
  const patientDataAbortRef = useRef(null);
  // nhsIds of the in-flight bulk, so a second notify for the same page does not start another.
  const patientDataInFlightKeyRef = useRef(null);
  // Failures stay here so a later notify does not retry the same patient on every page view.
  const patientDataFailedRef = useRef(new Set());

  useEffect(() => () => patientDataAbortRef.current?.abort(), []);

  // Kept referentially stable because Table's page-notify effect lists this callback as a
  // dependency. The cache is read through a ref so this does not need to close over
  // `nhsIdToPatientData` — putting that in the deps would recreate the callback on every arrival
  // and re-run the table effect.
  const handleCurrentPageRowsChange = useCallback((currentPageRows) => {
    if (!Array.isArray(currentPageRows) || currentPageRows.length === 0) {
      patientDataAbortRef.current?.abort();
      patientDataAbortRef.current = null;
      patientDataInFlightKeyRef.current = null;
      return;
    }

    const cached = nhsIdToPatientDataRef.current;
    const failed = patientDataFailedRef.current;

    const seen = new Set();
    const rowsToFetch = currentPageRows.filter(row => {
      if (!row.nhsId || !row.patientId || !row[idKey]) return false;
      if (cached.has(row.nhsId) || failed.has(row.nhsId)) return false;
      if (seen.has(row.nhsId)) return false;
      seen.add(row.nhsId);
      return true;
    });

    if (rowsToFetch.length === 0) {
      patientDataAbortRef.current?.abort();
      patientDataAbortRef.current = null;
      patientDataInFlightKeyRef.current = null;
      return;
    }

    const fetchKey = rowsToFetch.map(row => row.nhsId).join(',');
    if (patientDataInFlightKeyRef.current === fetchKey) {
      return;
    }

    patientDataAbortRef.current?.abort();
    const controller = new AbortController();
    patientDataAbortRef.current = controller;
    patientDataInFlightKeyRef.current = fetchKey;

    service.fetchPatientsData(
      rowsToFetch.map(row => row[idKey]),
      { signal: controller.signal }
    ).then(rowIdToPatient => {
      if (patientDataAbortRef.current !== controller) return;

      const arrivals = [];
      rowsToFetch.forEach(row => {
        const patient = rowIdToPatient?.[row[idKey]];
        if (patient) {
          arrivals.push([row.nhsId, patient]);
        } else {
          failed.add(row.nhsId);
        }
      });
      if (arrivals.length === 0) return;

      setNhsIdToPatientData(currentMap => {
        const next = new Map(currentMap);
        arrivals.forEach(([nhsId, patient]) => next.set(nhsId, patient));
        return next;
      });
    }).catch(error => {
      if (isAbortError(error)) return;
      console.error('Error fetching patient data for current page:', error);
      rowsToFetch.forEach(row => failed.add(row.nhsId));
    }).finally(() => {
      if (patientDataAbortRef.current !== controller) return;
      patientDataAbortRef.current = null;
      patientDataInFlightKeyRef.current = null;
    });
  }, [service, idKey]);

  return { nhsIdToPatientData, handleCurrentPageRowsChange };
};

export default useRowPatientNames;
